import { useEffect, useState } from "react";
import {
  AFTER_ILLUSTRATION_CHANGE,
  deleteIllustration,
  reorderIllustration,
  updateIllustration,
} from "@/api/mutations";
import { useAccao, texto } from "@/api/useAccao";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { longDate as dataLonga } from "@historia/shared/dates";
import type { IllustrationWithSrc as IlustracaoComSrc } from "@historia/shared/contracts";

type MarcoResumo = { id: string; titulo: string; data: string };

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-2.5 text-tinta outline-none transition-colors focus:border-ouro focus:ring-2 focus:ring-ouro/30";

export function EntradaConto({
  ilustracao,
  marcos,
  indice,
  total,
}: {
  ilustracao: IlustracaoComSrc;
  marcos: MarcoResumo[];
  indice: number;
  total: number;
}) {
  const [aEditar, setAEditar] = useState(false);
  const [aApagar, setAApagar] = useState(false);

  const [estado, acao] = useAccao(
    (fd: FormData) =>
      updateIllustration({
        id: ilustracao.id,
        titulo: texto(fd, "titulo"),
        descricao: texto(fd, "descricao") || null,
        data: texto(fd, "data") || null,
        marcoId: texto(fd, "marco_id") || null,
      }),
    { invalidate: AFTER_ILLUSTRATION_CHANGE },
  );

  const [, apagar] = useAccao((id: string) => deleteIllustration(id), {
    invalidate: AFTER_ILLUSTRATION_CHANGE,
  });

  const [, mover] = useAccao(
    (direction: "up" | "down") => reorderIllustration({ id: ilustracao.id, direction }),
    { invalidate: AFTER_ILLUSTRATION_CHANGE },
  );

  useEffect(() => {
    if (aEditar && estado.ok) setAEditar(false);
  }, [estado.ok, aEditar]);

  async function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await acao(new FormData(event.currentTarget));
  }

  // Imagem à esquerda nas entradas pares, à direita nas ímpares (alterna).
  const imagemDireita = indice % 2 === 1;



  return (
    <article className="grid items-center gap-6 sm:grid-cols-2">
      {/* Imagem */}
      <div className={imagemDireita ? "sm:order-2" : ""}>
        {ilustracao.src ? (
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-ouro/20 shadow-md">
            <img
              src={ilustracao.src}
              alt={ilustracao.titulo}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex aspect-[4/3] items-center justify-center rounded-3xl border border-ouro/20 bg-creme-200/50 text-sm text-tinta-700/60">
            (imagem indisponível)
          </div>
        )}
      </div>

      {/* Texto / edição */}
      <div className={imagemDireita ? "sm:order-1" : ""}>
        {aEditar ? (
          <form onSubmit={submeter} className="flex flex-col gap-3 text-left">
            <input type="hidden" name="id" value={ilustracao.id} />
            <input
              name="titulo"
              required
              maxLength={120}
              defaultValue={ilustracao.titulo}
              className={`${campo} font-display text-lg`}
            />
            <textarea
              name="descricao"
              rows={5}
              defaultValue={ilustracao.descricao ?? ""}
              placeholder="Descrição do momento…"
              className={`${campo} resize-none`}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <input type="date" name="data" defaultValue={ilustracao.data ?? ""} className={campo} />
              <select name="marco_id" defaultValue={ilustracao.marco_id ?? ""} className={campo}>
                <option value="">— Sem marco —</option>
                {marcos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.titulo}
                  </option>
                ))}
              </select>
            </div>
            {estado.erro && <p className="text-sm text-tinta">{estado.erro}</p>}
            <div className="flex items-center gap-2">
              <button
                type="submit"
                className="rounded-full bg-ouro px-5 py-2 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600"
              >
                Guardar
              </button>
              <button
                type="button"
                onClick={() => setAEditar(false)}
                className="rounded-full px-4 py-2 text-sm font-semibold text-tinta-700 hover:bg-creme-200"
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          <div className="text-left">
            <h2 className="font-display text-2xl text-tinta">{ilustracao.titulo}</h2>
            {ilustracao.data && (
              <p className="mt-1 text-sm text-tinta-700/80">{dataLonga(ilustracao.data)}</p>
            )}
            {ilustracao.descricao && (
              <p className="mt-3 whitespace-pre-line leading-relaxed text-tinta-700">
                {ilustracao.descricao}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-ouro/10 pt-3">
              <button
                type="button"
                onClick={() => void mover("up")}
                disabled={indice === 0}
                aria-label="Mover para cima"
                className="rounded-full px-2.5 py-1 text-sm text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => void mover("down")}
                disabled={indice === total - 1}
                aria-label="Mover para baixo"
                className="rounded-full px-2.5 py-1 text-sm text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => setAEditar(true)}
                className="rounded-full px-3 py-1 text-xs font-semibold text-ouro-600 transition-colors hover:bg-creme-200"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => setAApagar(true)}
                className="rounded-full border border-blush/60 px-3 py-1 text-xs font-semibold text-tinta/80 transition-colors hover:bg-blush-300/30"
              >
                Apagar
              </button>
            </div>
          </div>
        )}
      </div>

      <Confirmacao
        aberto={aApagar}
        titulo="Apagar ilustração?"
        mensagem={`Apagar "${ilustracao.titulo}"? A imagem é removida do conto e do armazenamento.`}
        textoConfirmar="Apagar"
        destrutivo
        onCancelar={() => setAApagar(false)}
        onConfirmar={() => {
          setAApagar(false);
          void apagar(ilustracao.id);
        }}
      />
    </article>
  );
}
